import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Edmonton = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/alberta-neighbourhoods/'

  const rentReportLink = isChinese
    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/'
    : 'https://liv.rent/blog/rent-reports/alberta/'

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.edmonton:About Edmonton Paragraph 1">
        <Paragraph>
          Edmonton is the capital city of{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText> and the
          province’s second-largest city next to Calgary with a population of
          around 1,000,000 people. Although it’s most commonly known for its oil
          & gas industries, Edmonton is also recognized as the &quot;Festival
          City of Canada&quot; owing to its wide variety of festivals and events
          taking place seemingly every day of the year. The city is also home to
          the largest shopping centre in Canada, West Edmonton Mall which has
          over 5.3 million sq. ft. of retail, dining, entertainment & more. Of
          course, Edmonton is also recognized for its abundant natural beauty –
          like Elk Island National Park, for example.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={rentReportLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,200 - $2,400',
          bed3: '$1,600 - $2,600'
        }}
        rentReportCity={t('cities:city_name_edmonton')}
        rentReportLink={rentReportLink}
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.edmonton:Apartment Hunting in Edmonton Paragraph 1">
            Finding affordable rentals in Edmonton is easier compared to other
            major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , especially if you’re looking for{' '}
            <LinkText href={links.under1000}>options under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.edmonton:Apartments for rent Edmonton Paragraph">
              Apartments are one of the most common form of housing you’ll find
              in Edmonton. Renters should be prepared for slightly higher prices
              for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Edmonton
              </LinkText>
              , compared to other rental types. Still, diligent renters should
              be able to find something within their budget in this sprawling
              metropolis.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.edmonton:Houses for rent Edmonton Paragraph">
              Though the city is very densely populated, there are quieter areas
              of Edmonton surrounding the city’s downtown where renters may find{' '}
              <LinkText href={links.house}>
                houses for rent in Edmonton
              </LinkText>
              . Both partial houses and entire houses for rent are common, with
              a mix of older and newer homes depending on which part of the city
              you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.edmonton:Basements for rent Alberta Paragraph">
              A popular alternative for students,{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent in {{ cityName }} as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.edmonton:Condos for rent Alberta Paragraph">
              Particularly in more densely-populated areas like Downtown{' '}
              {{ cityName }},{' '}
              <LinkText href={links.apartment}>
                condos for rent in {{ cityName }}
              </LinkText>{' '}
              are often the most common form of rental housing. These types of
              rental accommodation are perfect for those who like to be in the
              centre of the action, and don’t need too much space. If you are
              looking to rent in the city centre though, be prepared for
              significantly higher monthly rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals CITY Paragraph">
              {{ cityName }} has plenty of work opportunities and world-class
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in {{ cityName }}
              </LinkText>{' '}
              are exceedingly popular, particularly during the summer months and
              at the beginning of school semesters. The added flexibility is an
              appealing prospect for many renters, and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.edmonton:Cheap rentals Edmonton Paragraph">
              Compared to other Canadian cities, rent prices in Edmonton are
              relatively cheaper. As a result, it shouldn’t be too hard to find
              something within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Edmonton, AB
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Edmonton where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.edmonton:Living in Edmonton Paragraph">
          Living in Edmonton offers a vibrant urban experience amidst a backdrop
          of natural beauty. As Canada’s fifth-largest city, it boasts a diverse
          cultural scene, numerous festivals, and a wide range of dining and
          entertainment options. The city’s extensive river valley park system
          provides ample opportunities for outdoor activities. However, winters
          can be harsh, with cold temperatures and snow. Despite this, there are
          many welcoming{' '}
          <LinkText href={neighborhoodsLink}>
            neighbourhoods available for rent in Edmonton
          </LinkText>
          , making it a great place to call home.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.edmonton:Popular Neighbourhoods Edmonton Paragraph">
          Like any city, Edmonton is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Strathcona, Downtown, Oliver, Glenora, Terwillegar, Riverdale,
          and Garneau. For more info about Edmonton, you can consult our blog on{' '}
          <LinkText href={neighborhoodsLink}>Edmonton neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.edmonton:Local Landmarks Edmonton Paragraph">
          West Edmonton Mall continues to draw in tourists as well as providing
          everything Edmonton residents could ever ask for under one roof. The
          city also has Canada’s largest living history museum at Ford Edmonton
          Park, which is a great place to get up close and personal with the
          city’s history. For residents looking to immerse themselves in the
          city’s vibrant arts and culture scene, Whyte Avenue has plenty of
          theaters and trendy destinations. In the winter, be sure to check out
          the famous Ice Castles, an awe-inspiring display of natural beauty –
          not to mention a great place for a photo.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Edmonton
